/// <reference types='google.maps' />
import { NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
  Signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import isEqual from 'fast-deep-equal';
import {
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  filter,
  first,
  map,
  Observable,
  switchMap,
  tap,
} from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { PropsOf, TagConfig } from '@blaze-runtime/engine-domain';
import { markEventAsHandled } from '@blaze-runtime/engine-util';
import { accountSelectors } from '@blaze-shared/app-state-data-access';
import {
  CanvasElementPropertiesWithData,
  ContainerElementMetaUiFeatures,
  ElementLabelProps,
  getActiveBinding,
  isContainerElementMetaUiFeatures,
  makeBorderStyles,
  makeContainerVisualEffectStyles,
  PropertiesConfigInputContainerItemActionModel,
  PropertiesConfigInputListCustomFieldModel,
  RuntimeContext,
} from '@blaze-shared/elements-domain';
import {
  BlazeSchema,
  convertBlazeSchemaToElementSchema,
  DatasetRow,
  findColumn,
  getIdColumnName,
} from '@blaze-shared/schema-domain';
import { isDeepEqual } from '@blaze-shared/shared-util';

import { ElementFilterHeaderComponent } from '../../components/element-filter-header/element-filter-header.component';
import { ContainerActionsService } from '../../services/container-actions.service';
import { CanvasElementBaseComponent } from '../__base/base.component';

import { GoogleMapService } from './google-map/google-map.service';
import { GoogleMapComponent, MapOptions } from './google-map/map/google-map.component';
import { MapAdvancedMarkerComponent } from './google-map/map-advanced-marker/map-advanced-marker.component';
import { MapInfoWindowDirective } from './google-map/map-info-window/map-info-window.directive';
import { MapMarkerClustererComponent } from './google-map/map-marker-clusterer/map-marker-clusterer.component';
import { LocationService } from './location.service';
import { MapApiService } from './map-api.service';
import { MapCardComponent } from './map-card/map-card.component';
import { MapMarkerComponent } from './map-marker/map-marker.component';
import {
  DEFAULT_MAP_COORDS,
  InitialMapLocation,
  MapCoords,
  MapFieldType,
  MapMarker,
  MapPropsModel,
  MapType,
  MarkerStyle,
} from './map.models';

@UntilDestroy()
@Component({
  selector: 'be-map',
  imports: [
    NgStyle,
    ElementFilterHeaderComponent,
    GoogleMapComponent,
    MapMarkerClustererComponent,
    MapAdvancedMarkerComponent,
    MapMarkerComponent,
    MapInfoWindowDirective,
    MapCardComponent,
  ],
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.--map-background-color]': 'backgroundColor()',
    '[style.--map-border-radius]': 'borderRadius()',
    '[style.--map-internal-padding]': 'internalPadding()',
    '[style.--map-cluster-fill-color]': 'clusterFillColor()',
    '[style.--map-cluster-border-color]': 'clusterBorderColor()',
    '[style.--map-cluster-text-color]': 'clusterTextColor()',
  },
})
export class MapComponent extends CanvasElementBaseComponent<'map'> implements OnInit, OnDestroy {
  private readonly googleMapsService = inject(GoogleMapService);
  private readonly mapApiService = inject(MapApiService);
  private readonly locationService = inject(LocationService);
  private readonly containerActionsService = inject(ContainerActionsService);

  value = input<DatasetRow[]>([]);

  private readonly mapInfoWindow = viewChild.required(MapInfoWindowDirective);

  private readonly dataValue = this.getDataInput('value');
  private readonly elementIdSig = toSignal(this.elementId$);
  private readonly initialLocationSig = toSignal(
    this.props$.pipe(
      map((props) => props?.content.location?.initial),
      distinctUntilChanged(isEqual),
    ),
  );

  protected readonly backgroundColor = signal<string | undefined>(undefined);
  protected readonly borderRadius = signal<string | undefined>(undefined);
  protected readonly internalPadding = signal<string | undefined>(undefined);
  protected readonly clusterFillColor = signal<string | undefined>(undefined);
  protected readonly clusterBorderColor = signal<string | undefined>(undefined);
  protected readonly clusterTextColor = signal<string | undefined>(undefined);

  readonly elementActionsKeyRoot = 'b-lac';
  readonly elementActionsKey = computed(() => {
    const elementId = this.elementIdSig();
    return elementId ? `${this.elementActionsKeyRoot}-${elementId}` : '';
  });

  titleFontColor = signal<string | undefined>(undefined);
  wrapperStyles = signal<Record<string, string>>({});
  tagConfig: Signal<TagConfig | undefined>;

  isGoogleMapInitialized = toSignal(this.googleMapsService.initialized$, { initialValue: false });
  title = signal<ElementLabelProps | undefined>(undefined);

  mapId = signal<string | undefined>(undefined);
  center = signal<MapCoords>(DEFAULT_MAP_COORDS);
  zoom = signal(8);
  options = signal<MapOptions>({
    enableZoom: true,
    mapType: MapType.Road,
  });

  markerStyle = signal(MarkerStyle.Pin);
  markerClustering = signal(true);

  markers = signal<MapMarker[]>([]);
  selectedMarkerId = signal<string | null>(null);
  selectedMarker = computed(() => {
    const id = this.selectedMarkerId();
    if (!id) {
      return null;
    }
    return this.markers().find((marker) => marker.id === id) ?? null;
  });

  labelEnabled = computed(() => this.title()?.enabled ?? false);
  labelText = computed(() => this.title()?.label ?? '');
  elementKey = computed(() => `map-${this.elementId}`);

  constructor() {
    super();

    const dataSource$ = this.props$.pipe(map((props) => getActiveBinding(props?.data)?.dataSource));

    this.tagConfig = toSignal(
      this.createTagConfig(
        this.props$.pipe(map((props) => props?.content.card.customFields)),
        this.props$.pipe(map((props) => props?.content.columns)),
        this.createPaletteSchemaObservable(dataSource$),
        this.dataValue.data$,
      ),
    );

    this.initMarginHandler((props) => props?.style?.map?.offset?.margin);

    effect(() => {
      const initial = this.initialLocationSig();
      if (initial) {
        this.updateMapLocation(initial);
      }
    });
  }

  ngOnInit(): void {
    this.initializeGoogleMaps().subscribe();
    this.updateDefaultEventSchema();

    combineLatest({
      props: this.props$,
      dataInput: this.dataValue.dataInput$,
    })
      .pipe(
        map(({ props, dataInput }) => ({
          props,
          data: dataInput?.data as DatasetRow[],
          schema: dataInput?.schema,
        })),
        untilDestroyed(this),
      )
      .subscribe(({ props, data, schema }) => {
        if (!props?.content) {
          return;
        }

        this.title.set(props.content.title);

        if (props.content.markers) {
          this.markerStyle.set(props.content.markers.style);
          this.markerClustering.set(props.content.markers.clustering);
        }

        if (props.content.location?.zoom) {
          this.zoom.set(+props.content.location.zoom);
        }

        this.options.set({
          enableZoom: props.content.options?.enableZoom ?? false,
          mapType: props.content.options?.type ?? MapType.Road,
        });

        if (schema) {
          this.markers.set(this.createMarkers(data, schema));
        }

        this.updateStyles(props.style);
      });

    this.initActionStyles(
      this.elementActionsKey(),
      this.props$.pipe(map((props) => props?.content.card?.actions ?? [])),
      ['action-item'],
    );
  }

  ngOnDestroy(): void {
    this.stylesCleanup.dispose(this.elementKey());
  }

  onMouseDown(event: Event): void {
    markEventAsHandled(event);
  }

  openInfoWindow(markerElement: google.maps.marker.AdvancedMarkerElement, marker: MapMarker): void {
    if (!marker.fields.card?.title) {
      this.mapInfoWindow().close();
      this.selectedMarkerId.set(null);
      return;
    }

    this.selectedMarkerId.set(marker.id);
    this.mapInfoWindow().open(markerElement);

    this.cdr.detectChanges();
  }

  onCardAction(marker: MapMarker, action: PropertiesConfigInputContainerItemActionModel): void {
    const dataSource = getActiveBinding(
      (this.props as CanvasElementPropertiesWithData)?.data,
      'value',
    )?.dataSource;
    if (!dataSource) {
      return console.error(`Action "${action.label}" failed. Data source not configured.`);
    }

    this.containerActionsService.handleListlikeAction({
      dataSource,
      action,
      props: undefined,
      output: this.output,
      dataRow: marker.item,
      dataRowId: marker.id,
      dataRowSchema: this.getEventSchema('action'),
    });
  }

  private updateStyles(styles: Partial<MapPropsModel['style']>): void {
    this.titleFontColor.set(styles.map?.title.color1);
    this.backgroundColor.set(styles.map?.background.color1);
    this.borderRadius.set(styles.map?.radius);
    this.internalPadding.set(styles.map?.offset.padding);

    this.clusterFillColor.set(styles.marker?.selected.fill.color1);
    this.clusterBorderColor.set(styles.marker?.selected.outline.color1);
    this.clusterTextColor.set(styles.marker?.selected.label.color1);

    this.wrapperStyles.set({
      ...makeContainerVisualEffectStyles(styles.map?.effect),
      ...makeBorderStyles(styles?.container?.outline, styles?.container?.outlineWidth),
    });

    const elementKey = this.elementKey();
    this.stylesCleanup.register(elementKey);
    const cardEffects = makeContainerVisualEffectStyles(styles.card?.effect) ?? {};

    this.stylesCleanup.update(
      elementKey,
      `
      .${elementKey} div[role=dialog].gm-style-iw.gm-style-iw-c {
        border-radius: ${styles.card?.radius};
        ${Object.entries(cardEffects)
          .map(([key, value]) => key + ':' + value)
          .join('\n')}
      }
      .${elementKey} .gm-style .gm-style-iw-tc::after {
        background-color: ${styles.card?.background.color1}
      }
    `,
    );
  }

  private updateMapLocation(location: MapPropsModel['content']['location']['initial']): void {
    if (location.type === InitialMapLocation.Current) {
      this.centerOnCurrentLocation();
    } else if (location.type === InitialMapLocation.Selection && location.coords) {
      this.center.set(location.coords);
    }
  }

  private centerOnCurrentLocation(): void {
    this.locationService.getCurrentLocation().subscribe({
      next: (location) => {
        this.center.set({
          lat: location.coords.latitude,
          lng: location.coords.longitude,
        });
      },
    });
  }

  private initializeGoogleMaps(): Observable<any> {
    return this.googleMapsService.mapId$.pipe(
      first(),
      switchMap((mapId) => {
        if (mapId) {
          this.mapId.set(mapId);
          return EMPTY;
        }

        return this.loadMapId();
      }),
    );
  }

  private loadMapId(): Observable<unknown> {
    return this.store.select(accountSelectors.selectAccountId).pipe(
      switchMap((accountId) => this.mapApiService.getApiKey(accountId)),
      first(),
      tap(({ apiKey, maps }) => {
        if (!maps?.length) {
          throw new Error('Missing map id');
        }
        this.mapId.set(maps[0].id);
        this.googleMapsService.appendGoogleMapsScript(apiKey, maps[0].id);
      }),
    );
  }

  private createMarkers(data: DatasetRow[], schema: BlazeSchema): MapMarker[] {
    if (!this.props?.content.markers || !this.props?.content.input?.coordinates) {
      return [];
    }

    const idColumn = getIdColumnName(schema);
    const coordsColumn = findColumn(schema.columns, this.props.content.input.coordinates);

    if (!idColumn || !coordsColumn) {
      return [];
    }

    if (coordsColumn.dataType !== 'GeoPoint') {
      console.error('Invalid data type for the coordinates field');
      return [];
    }

    const { markers, card } = this.props.content;
    const titleColumn = findColumn(schema.columns, markers.title);
    const imageColumn = findColumn(schema.columns, markers.image);

    if (
      (this.markerStyle() === MarkerStyle.Image && !imageColumn) ||
      (this.markerStyle() === MarkerStyle.Bubble && !titleColumn)
    ) {
      return [];
    }

    return data
      .map((row) => {
        const position = row[coordsColumn.name];

        const marker: MapMarker = {
          id: row[idColumn],
          position: {
            lat: position?.coordinates ? position.coordinates[1] : null,
            lng: position?.coordinates ? position.coordinates[0] : null,
          },
          item: row,
          fields: {
            title: titleColumn,
            field: findColumn(schema.columns, markers.field),
            image: imageColumn,
            customFields$: this.getCustomFields$('marker', row),
            card: this.props?.content.card?.enabled
              ? {
                  title: findColumn(schema.columns, card?.title),
                  description: findColumn(schema.columns, card?.description),
                  image: findColumn(schema.columns, card?.image),
                  customFields$: this.getCustomFields$('card', row),
                  actions$: this.getActions$(row),
                }
              : undefined,
          },
        };

        return marker;
      })
      .filter((marker) => marker.position?.lat && marker.position?.lng);
  }

  private getActions$(
    row: DatasetRow,
  ): Observable<PropertiesConfigInputContainerItemActionModel[]> {
    return combineLatest({
      context: this.runtimeContext$,
      actions: this.props$.pipe(
        map((props) =>
          props?.content.card?.actions.filter((action) => action.label || action.icon),
        ),
        distinctUntilChanged(isDeepEqual),
      ),
    }).pipe(
      map(({ context, actions }) => {
        if (!actions) {
          return [];
        }

        const normalizedContext = this.normalizeLinkedParams(context);

        const rowContext = {
          ...normalizedContext,
          output: this.normalizeLinkedParams(row as RuntimeContext),
        };

        return actions.filter(
          (action) =>
            !action.visibilityExpression ||
            !!this.evaluateExpression(action.visibilityExpression, rowContext, true),
        );
      }),
    );
  }

  private getCustomFields$(
    type: MapFieldType,
    row: DatasetRow,
  ): Observable<PropertiesConfigInputListCustomFieldModel[] | undefined> {
    return combineLatest({
      context: this.runtimeContext$,
      customFieldsProps: this.props$.pipe(
        map((props) => getCustomFieldsForType(props?.content, type)),
        distinctUntilChanged(isDeepEqual),
      ),
      customFieldsMeta: this.store
        .select(runtimeSelectors.selectElementMetaById(this.elementId))
        .pipe(
          map((elementMeta) =>
            isContainerElementMetaUiFeatures(elementMeta.uiFeatures)
              ? (
                  elementMeta.uiFeatures as ContainerElementMetaUiFeatures<MapFieldType>
                )?.columns?.filter(({ fieldType }) => fieldType === type)
              : undefined,
          ),
          distinctUntilChanged(isDeepEqual),
        ),
    }).pipe(
      map(({ context, customFieldsProps, customFieldsMeta }) => {
        const customFields = customFieldsProps
          ?.map((propsField) => {
            const metaField = findColumn(customFieldsMeta, propsField.column);
            return {
              ...propsField,
              column: this.getColumnName(propsField.column) ?? '',
              label: metaField?.name || propsField.label,
              displayFormat: metaField?.format || propsField.displayFormat,
              precision: metaField?.precision ?? propsField.precision,
              currency: metaField?.currency ?? propsField.currency,
              duration: metaField?.duration ?? propsField.duration,
              dateFormat: metaField?.dateFormat ?? propsField.dateFormat,
              dateTimeFormat: metaField?.dateTimeFormat ?? propsField.dateTimeFormat,
            };
          })
          ?.filter(({ column }) => column);

        if (!customFields) {
          return [];
        }

        if (!customFields?.some((field) => !!field.visibilityExpression)) {
          return customFields ?? [];
        }

        const normalizedContext = this.normalizeLinkedParams(context);

        const rowContext = {
          ...normalizedContext,
          output: this.normalizeLinkedParams(row as RuntimeContext),
        };

        return customFields.filter(
          (field) =>
            !field.visibilityExpression ||
            !!this.evaluateExpression(field.visibilityExpression, rowContext, true),
        );
      }),
    );
  }

  private updateDefaultEventSchema(): void {
    if (!this.bindingResults?.value) {
      this.setDefaultEventSchema('action');
    }

    this.dataValue.schema$
      .pipe(filter(Boolean), distinctUntilChanged(isDeepEqual), untilDestroyed(this))
      .subscribe((schema: BlazeSchema) => {
        this.setDefaultEventSchema(
          'action',
          convertBlazeSchemaToElementSchema(schema, this.elementId, 'action'),
        );
      });
  }
}

function getCustomFieldsForType(
  content: PropsOf<'map'>['content'] | undefined,
  type: string,
): PropertiesConfigInputListCustomFieldModel[] {
  let customFields: PropertiesConfigInputListCustomFieldModel[] = [];

  if (type === 'card') {
    customFields = content?.card?.customFields || [];
  } else if (type === 'marker') {
    customFields = content?.markers?.customFields || [];
  }

  return customFields.filter((field) => field.column);
}
